import { motion } from "framer-motion";
import { Check, Lock } from "lucide-react";

const ASSURANCES = [
  "Server-side sessions, HttpOnly cookies, nothing in local storage",
  "Argon2id password hashing, 12-character passphrase floor",
  "Authenticator MFA required for every staff identity",
  "Every sign-in, reset and revoke is written to the audit ledger",
];

export function AuthLayout({ eyebrow, title, subtitle, children, footer }) {
  return (
    <div className="min-h-screen bg-graphite-950 lg:grid lg:grid-cols-12">
      <aside className="bz-grain relative hidden overflow-hidden border-r border-white/8 bg-graphite-900 lg:col-span-5 lg:flex lg:flex-col lg:justify-between lg:p-12 xl:p-16">
        <div className="bz-grid-bg absolute inset-0" />
        <div className="pointer-events-none absolute -left-32 top-1/3 h-96 w-96 rounded-full bg-electric/10 blur-[120px]" />
        <div className="relative">
          <div className="flex items-center gap-3">
            <span className="font-display text-2xl font-black tracking-[0.18em] text-white">BIZII</span>
            <span className="bz-badge border-electric/30 bg-electric/10 text-electric">Platform</span>
          </div>
          <p className="bz-caption mt-3">Canonical identity · Phase 0</p>
        </div>
        <div className="relative max-w-md">
          <h2 className="font-display text-4xl font-bold leading-[1.05] tracking-tight text-white xl:text-5xl">
            One identity.
            <br />
            Every BIZII surface.
          </h2>
          <ul className="mt-10 space-y-4">
            {ASSURANCES.map((item, index) => (
              <motion.li
                key={item}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.15 + index * 0.08, duration: 0.35 }}
                className="flex items-start gap-3 text-sm text-slate-300"
              >
                <span className="mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded-full border border-electric/40 bg-electric/10">
                  <Check className="h-3 w-3 text-electric" strokeWidth={2.5} />
                </span>
                {item}
              </motion.li>
            ))}
          </ul>
        </div>
        <div className="relative flex items-center gap-3 text-xs text-slate-400">
          <span className="bz-live-dot h-2 w-2 rounded-full bg-electric" />
          <span className="font-mono uppercase tracking-[0.18em]">Secure channel</span>
          <span className="text-slate-600">·</span>
          <Lock className="h-3.5 w-3.5" strokeWidth={1.75} />
          <span>Host-only session cookie</span>
        </div>
      </aside>

      <main className="relative flex min-h-screen flex-col lg:col-span-7">
        <header className="flex items-center justify-between px-6 py-6 lg:hidden">
          <span className="font-display text-xl font-black tracking-[0.18em] text-white">BIZII</span>
          <span className="bz-caption">Phase 0</span>
        </header>
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25, ease: "easeOut" }}
          className="flex flex-1 items-center px-6 pb-16 pt-4 sm:px-12 lg:px-20 xl:px-28"
        >
          <div className="w-full max-w-md">
            <div className="bz-corner relative rounded-xl border border-white/10 bg-graphite-900/90 p-7 shadow-2xl backdrop-blur-xl sm:p-9">
              <p className="bz-caption text-electric">{eyebrow}</p>
              <h1 className="mt-3 font-display text-3xl font-bold tracking-tight text-white sm:text-4xl">{title}</h1>
              {subtitle ? <p className="mt-2 text-sm leading-relaxed text-slate-400">{subtitle}</p> : null}
              <div className="mt-8">{children}</div>
            </div>
            {footer ? <div className="mt-6 text-sm text-slate-400">{footer}</div> : null}
          </div>
        </motion.div>
      </main>
    </div>
  );
}
